import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n/LanguageContext";
import { useUser } from "../context/UserContext";
import reportingAPI from "../services/ReportingAPI";
import { formatDateOnly, formatDateTime } from "../utils/DateTimeUtil";
import "./css/ShieldReports.css";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * Shield Report Detail Page
 * Displays full report content with all sections.
 *
 * Props:
 *  reportUuid    - UUID of the report to fetch (required when initialReport is not provided)
 *  setHeroContent - Navigation callback used when onBack is not provided
 *  initialReport  - Pre-fetched PersonalShieldReportDTO; skips the internal fetch when provided
 *  onBack         - Custom back callback; overrides the default "Back to Reports" navigation
 */
function ShieldReportDetail({
  reportUuid,
  setHeroContent,
  initialReport,
  onBack,
}) {
  const { loggedInUser } = useUser();
  const { t } = useTranslation();
  const [report, setReport] = useState(initialReport || null);
  const [loading, setLoading] = useState(!initialReport);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    if (initialReport) return; // already have data
    if (reportUuid) {
      fetchReport();
    }
  }, [reportUuid]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await reportingAPI.getReport(reportUuid);
      setReport(response);
    } catch (err) {
      console.error("[ShieldReportDetail] Error fetching report:", err);
      setError(
        "Failed to load report. It may have been deleted or you don't have access.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      await reportingAPI.downloadPdfToDevice(reportUuid || report?.reportUuid);
    } catch (err) {
      console.error("[ShieldReportDetail] Error downloading PDF:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleResendEmail = async () => {
    try {
      setEmailing(true);
      await reportingAPI.resendReportEmail(
        reportUuid || report?.reportUuid,
        false,
      );
      toast.success(t("shieldReportDetail.toast.emailSent"));
    } catch (err) {
      console.error("[ShieldReportDetail] Error sending email:", err);
      toast.error(t("shieldReportDetail.toast.emailFailed"));
    } finally {
      setEmailing(false);
    }
  };

  const handleBackToList = () => {
    if (onBack) {
      onBack();
    } else {
      setHeroContent("Shield Reports");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="shield-report-detail">
        <LoadingSpinner message="Loading your report..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="shield-report-detail">
        <div className="report-error">
          <span className="error-icon">⚠️</span>
          <h3>Unable to Load Report</h3>
          <p>{error}</p>
          <button onClick={handleBackToList}>Back to Reports</button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const {
    shieldScoreSummary,
    simulationPerformance,
    trainingProgress,
    vulnerabilityInsights,
    scamAnalysisActivity,
    personalizedTips,
    goals,
  } = report;

  const scoreColor = reportingAPI.getScoreColor(
    shieldScoreSummary?.currentScore || 0,
  );
  const trend = reportingAPI.formatTrend(
    shieldScoreSummary?.trendDirection,
    shieldScoreSummary?.trendPercentage || 0,
  );

  return (
    <div className="shield-report-detail">
      {/* Back Navigation */}
      <button className="back-btn" onClick={handleBackToList}>
        ← Back to Reports
      </button>

      {/* Report Header */}
      <div className="report-detail-header">
        <div className="header-info">
          <h1>🛡️ Personal Shield Report</h1>
          <p className="report-period">
            {reportingAPI.formatPeriodLabel(
              report.periodType,
              report.periodStart,
              report.periodEnd,
            )}
          </p>
          <p className="report-generated">
            Generated {formatDateOnly(report.generatedAt, "long")}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="action-btn download"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "📥 Download PDF"}
          </button>
          <button
            className="action-btn email"
            onClick={handleResendEmail}
            disabled={emailing}
          >
            {emailing ? "Sending..." : "✉️ Email Report"}
          </button>
        </div>
      </div>

      {/* Shield Score Summary */}
      <section className="report-section score-section">
        <div className="score-main">
          <div
            className="score-circle large"
            style={{ borderColor: scoreColor }}
          >
            <span className="score-number" style={{ color: scoreColor }}>
              {shieldScoreSummary?.currentScore || 0}
            </span>
            <span className="score-level">
              {reportingAPI.formatScoreLevel(shieldScoreSummary?.scoreLevel)}
            </span>
          </div>

          {shieldScoreSummary?.trendDirection &&
            shieldScoreSummary.trendDirection !== "NEW" && (
              <div className="score-trend" style={{ color: trend.color }}>
                <span className="trend-icon">{trend.icon}</span>
                <span>{trend.text}</span>
                <span className="trend-from">
                  from {shieldScoreSummary.previousScore}
                </span>
              </div>
            )}
        </div>

        {/* Component Breakdown */}
        <div className="score-components">
          <h3>Score Breakdown</h3>
          <div className="component-bars">
            {[
              {
                name: "Simulation Performance",
                data: shieldScoreSummary?.simulationComponent,
                icon: "📧",
              },
              {
                name: "Training Progress",
                data: shieldScoreSummary?.trainingComponent,
                icon: "📚",
              },
              {
                name: "Scam Detection",
                data: shieldScoreSummary?.scamDetectionComponent,
                icon: "🔍",
              },
              {
                name: "Engagement",
                data: shieldScoreSummary?.engagementComponent,
                icon: "🎯",
              },
            ].map((comp, i) => (
              <div key={i} className="component-bar">
                <div className="component-header">
                  <span className="component-name">
                    {comp.icon} {comp.name}
                  </span>
                  <span className="component-score">
                    {comp.data?.score || 0}/100
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${comp.data?.score || 0}%`,
                      backgroundColor: reportingAPI.getScoreColor(
                        comp.data?.score || 0,
                      ),
                    }}
                  />
                </div>
                <div className="component-weight">
                  Weight: {comp.data?.weight || 0}% • Contribution:{" "}
                  {comp.data?.weightedScore || 0} pts
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simulation Performance */}
      {simulationPerformance && (
        <section className="report-section">
          <h2>📧 Phishing Simulation Results</h2>
          <p className="section-summary">
            {simulationPerformance.performanceSummary}
          </p>

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">
                {simulationPerformance.totalSimulations}
              </span>
              <span className="stat-label">Total Tests</span>
            </div>
            <div className="stat-card success">
              <span className="stat-value">
                {simulationPerformance.recognitionRate?.toFixed(0)}%
              </span>
              <span className="stat-label">Recognition Rate</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">
                {simulationPerformance.reportedCount}
              </span>
              <span className="stat-label">Reported</span>
            </div>
            <div className="stat-card warning">
              <span className="stat-value">
                {simulationPerformance.clickedCount}
              </span>
              <span className="stat-label">Clicked (Failed)</span>
            </div>
          </div>
        </section>
      )}

      {/* Training Progress */}
      {trainingProgress && (
        <section className="report-section">
          <h2>📚 Training Progress</h2>
          <p className="section-summary">{trainingProgress.progressSummary}</p>

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">
                {trainingProgress.completedThisPeriod}
              </span>
              <span className="stat-label">Completed This Period</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">
                {trainingProgress.completedTotal}
              </span>
              <span className="stat-label">Total Completed</span>
            </div>
            {trainingProgress.averageQuizScore > 0 && (
              <div className="stat-card success">
                <span className="stat-value">
                  {trainingProgress.averageQuizScore?.toFixed(0)}%
                </span>
                <span className="stat-label">Avg Quiz Score</span>
              </div>
            )}
            <div className="stat-card">
              <span className="stat-value">
                {trainingProgress.assignedPending}
              </span>
              <span className="stat-label">Pending</span>
            </div>
          </div>

          {trainingProgress.recentlyCompleted?.length > 0 && (
            <div className="training-list">
              <h4>Recently Completed</h4>
              {trainingProgress.recentlyCompleted.map((training, i) => (
                <div key={i} className="training-item">
                  <span className="training-name">{training.trainingName}</span>
                  {training.quizScore && (
                    <span className="training-score">
                      Score: {training.quizScore}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Vulnerability Insights */}
      {vulnerabilityInsights &&
        vulnerabilityInsights.topVulnerabilities?.length > 0 && (
          <section className="report-section vulnerabilities">
            <h2>⚠️ Areas for Improvement</h2>
            <p className="section-summary">{vulnerabilityInsights.summary}</p>

            <div className="vulnerability-list">
              {vulnerabilityInsights.topVulnerabilities.map((vuln, i) => (
                <div
                  key={i}
                  className={`vulnerability-card ${vuln.riskLevel?.toLowerCase()}`}
                >
                  <div className="vuln-header">
                    <span className="vuln-type">{vuln.displayName}</span>
                    <span
                      className={`vuln-risk risk-${vuln.riskLevel?.toLowerCase()}`}
                    >
                      {vuln.riskLevel} Risk
                    </span>
                  </div>
                  <p className="vuln-description">{vuln.description}</p>
                  {vuln.recommendedTraining && (
                    <div className="vuln-training">
                      💡 Recommended: {vuln.recommendedTraining}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

      {/* Personalized Tips */}
      {personalizedTips?.tips?.length > 0 && (
        <section className="report-section tips">
          <h2>💡 Tips for You</h2>

          <div className="tips-list">
            {personalizedTips.tips.map((tip, i) => (
              <div
                key={i}
                className={`tip-card priority-${tip.priority?.toLowerCase()}`}
              >
                <h4>{tip.title}</h4>
                <p>{tip.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Goals */}
      {goals && (
        <section className="report-section goals">
          <h2>🎯 Your Goals</h2>
          <p className="goals-summary">
            {goals.completedCount} of {goals.totalCount} goals completed
          </p>

          <div className="goals-list">
            {goals.goals?.map((goal, i) => (
              <div
                key={i}
                className={`goal-card ${goal.status?.toLowerCase()}`}
              >
                <div className="goal-header">
                  <span className="goal-title">{goal.title}</span>
                  <span
                    className={`goal-status status-${goal.status?.toLowerCase()}`}
                  >
                    {goal.status === "COMPLETED"
                      ? "✓ Completed"
                      : "In Progress"}
                  </span>
                </div>
                <p className="goal-description">{goal.description}</p>
                <div className="goal-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${goal.progressPercentage}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {goal.currentValue} / {goal.targetValue}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <div className="report-footer">
        <p>
          Thank you for using KinShield to protect yourself and your family
          online.
        </p>
        <p className="footer-date">
          Report ID: {report.reportId?.substring(0, 8)}... | Generated:{" "}
          {formatDateTime(report.generatedAt)}
        </p>
      </div>
    </div>
  );
}

export default ShieldReportDetail;
